import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  aiSupportApi,
  useGetAiConversationsQuery,
} from "src/_api/aiSupportApi";
import { CONVERSATION_STATUS } from "src/_modules/AiSupportChat/core/protocol";

/** Rows per page; page 1 uses the same args as every other caller. */
export const PAGE_SIZE = 20;

/**
 * One list out of several pages (newest first):
 * - de-duplicated by id, first occurrence wins: a conversation created after
 *   page 1 was fetched shifts every page by one, so the next page repeats
 *   the last row of the previous one;
 * - the open conversation pinned first (plan §3.5). The backend keeps at
 *   most one open, and it is normally the newest, so this rarely moves
 *   anything.
 *
 * @param {Array<Array<{ id: string, status?: string }> | null | undefined>} pages
 * @returns {Array<object>}
 */
export const mergeConversationPages = (pages) => {
  const seen = new Set();
  const merged = [];
  pages.forEach((docs) => {
    (Array.isArray(docs) ? docs : []).forEach((conversation) => {
      const id = conversation?.id;
      if (id === undefined || id === null || seen.has(id)) return;
      seen.add(id);
      merged.push(conversation);
    });
  });

  const openIndex = merged.findIndex(
    (conversation) => conversation.status === CONVERSATION_STATUS.OPEN
  );
  if (openIndex > 0) merged.unshift(...merged.splice(openIndex, 1));
  return merged;
};

/** Extra pages loaded on top of one page-1 result (`base`). */
const NO_MORE = Object.freeze({
  base: undefined,
  docs: [],
  last: null,
  status: "idle",
});

/**
 * "Your previous conversations" (plan §3.5, §5.1): page 1 is a normal
 * subscribed query, so `AiConversations` invalidation (a new conversation,
 * an answer done) and focus refetches keep it fresh. Later pages are fetched
 * on demand and accumulated here.
 *
 * The extra pages belong to the page-1 result they were loaded after: when
 * page 1 comes back different (RTK keeps the same `data` object when a
 * refetch returns equal data), the list starts again from the new page 1.
 *
 * @returns {{
 *   conversations: Array<object>,
 *   isLoading: boolean, isError: boolean, retry: () => void,
 *   hasMore: boolean, loadMore: () => void,
 *   isLoadingMore: boolean, loadMoreFailed: boolean,
 * }}
 */
export default function useConversationList() {
  const dispatch = useDispatch();
  // Per-user cache entries (the endpoint doesn't send `owner`)
  const owner = useSelector((state) => state.entities.profile.profile?.id);
  const first = useGetAiConversationsQuery({
    page: 1,
    limit: PAGE_SIZE,
    owner,
  });
  const base = first.data;
  const [more, setMore] = useState(NO_MORE);
  const inFlight = useRef(false);

  const current = more.base === base ? more : NO_MORE;
  const lastPage = current.last ?? base;
  const hasMore = Boolean(base) && lastPage?.hasNextPage === true;

  const loadMore = useCallback(() => {
    if (inFlight.current || !hasMore) return;
    inFlight.current = true;
    const page = lastPage.nextPage ?? (Number(lastPage.page) || 1) + 1;
    setMore({ ...current, base, status: "loading" });

    // Not the lazy hook: its subscription would keep the last page alive and
    // refetch it on every `AiConversations` invalidation, for nothing
    const request = dispatch(
      aiSupportApi.endpoints.getAiConversations.initiate(
        { page, limit: PAGE_SIZE, owner },
        { forceRefetch: true }
      )
    );
    request
      .unwrap()
      .then(
        (data) =>
          setMore((prev) =>
            prev.base === base
              ? {
                  base,
                  docs: [
                    ...prev.docs,
                    ...(Array.isArray(data?.docs) ? data.docs : []),
                  ],
                  last: data ?? { hasNextPage: false },
                  status: "idle",
                }
              : prev
          ),
        () =>
          setMore((prev) =>
            prev.base === base ? { ...prev, status: "error" } : prev
          )
      )
      .finally(() => {
        inFlight.current = false;
        request.unsubscribe();
      });
  }, [dispatch, base, current, lastPage, hasMore, owner]);

  const conversations = useMemo(
    () => mergeConversationPages([base?.docs, current.docs]),
    [base, current.docs]
  );

  const { refetch } = first;
  const retry = useCallback(() => refetch(), [refetch]);

  return {
    conversations,
    // Page 1 states only count while there is nothing to show: a failed
    // background refetch keeps the previous data (RTK) on screen
    isLoading: !base && first.isFetching,
    isError: !base && first.isError,
    retry,
    hasMore,
    loadMore,
    isLoadingMore: current.status === "loading",
    loadMoreFailed: current.status === "error",
  };
}
